import { CardInstance, CardDef } from '../types';
import { getCardById } from '../cards';
import { showCardDetail } from './CardDetailModal';

// カード移動選択モーダル
// ベンチから除外するカードを選択するUI

interface MoveModalState {
  cards: CardInstance[];
  maxSelect: number;
  selectedIndices: Set<number>;
  title: string;
  description: string;
  accentColor: string;
  onComplete: (selected: CardInstance[]) => void;
}

let modalState: MoveModalState | null = null;

export function showMoveCardModal(
  cards: CardInstance[],
  maxSelect: number,
  title: string,
  description: string,
  onComplete: (selected: CardInstance[]) => void
): void {
  modalState = {
    cards: [...cards],
    maxSelect: Math.min(maxSelect, cards.length),
    selectedIndices: new Set(),
    title,
    description,
    accentColor: '#e74c3c',
    onComplete,
  };

  renderMoveModal();
  bindMoveEvents();
}

function renderMoveModal(): void {
  if (!modalState) return;

  const existing = document.getElementById('moveCardModal');
  if (existing) existing.remove();

  const selectedCount = modalState.selectedIndices.size;
  const canConfirm = selectedCount > 0 && selectedCount <= modalState.maxSelect;
  const canSkip = modalState.maxSelect > 0;

  const modal = document.createElement('div');
  modal.id = 'moveCardModal';
  modal.innerHTML = `
    <div class="move-overlay" style="
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 3000;
      overflow-y: auto;
      padding: 20px;
    ">
      <div class="move-content" style="
        background: linear-gradient(145deg, #2e1e1e 0%, #1a1215 100%);
        border: 2px solid ${modalState.accentColor};
        border-radius: 16px;
        padding: 24px;
        max-width: 450px;
        width: 90%;
        max-height: 90vh;
        overflow-y: auto;
        box-shadow: 0 0 40px ${modalState.accentColor}50;
      ">
        <h2 style="color: ${modalState.accentColor}; margin: 0 0 8px 0; font-size: 1.2rem;">
          ${modalState.title}
        </h2>
        <p style="color: #aaa; margin: 0 0 16px 0; font-size: 0.85rem;">
          ${modalState.description}
        </p>
        
        <div style="background: rgba(231, 76, 60, 0.1); border: 1px solid ${modalState.accentColor}; border-radius: 8px; padding: 8px 12px; margin-bottom: 12px;">
          <span style="color: ${modalState.accentColor};">選択中: ${selectedCount} / ${modalState.maxSelect}枚まで</span>
        </div>
        
        <div id="moveCardList" style="
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 20px;
        ">
          ${modalState.cards.map((card, idx) => renderMoveCard(card, idx)).join('')}
        </div>
        
        <div style="display: flex; gap: 12px; justify-content: flex-end;">
          ${canSkip ? `
            <button id="btnMoveSkip" style="
              background: #555;
              border: none;
              color: #fff;
              padding: 10px 16px;
              border-radius: 8px;
              cursor: pointer;
              font-size: 0.85rem;
            ">スキップ</button>
          ` : ''}
          <button id="btnMoveConfirm" style="
            background: ${canConfirm ? modalState.accentColor : '#555'};
            border: none;
            color: ${canConfirm ? '#fff' : '#888'};
            padding: 10px 20px;
            border-radius: 8px;
            cursor: ${canConfirm ? 'pointer' : 'not-allowed'};
            font-weight: bold;
            font-size: 0.9rem;
          " ${canConfirm ? '' : 'disabled'}>✓ 確定</button>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(modal);
}

function renderMoveCard(card: CardInstance, idx: number): string {
  if (!modalState) return '';
  
  const def = getCardById(card.defId);
  if (!def) return '';

  const catColor = getCategoryColor(def.category);
  const catIcon = getCategoryIcon(def.category);
  const isSelected = modalState.selectedIndices.has(idx);
  
  // 所有者表示(_ownerIdx がある場合)
  const ownerIdx = (card as any)._ownerIdx;
  const ownerLabel = ownerIdx !== undefined 
    ? (ownerIdx === 0 ? '👤' : '👾') 
    : '';

  return `
    <div class="move-card" data-move-idx="${idx}" style="
      display: flex;
      align-items: center;
      gap: 12px;
      background: linear-gradient(90deg, ${catColor}22 0%, rgba(0,0,0,0.4) 100%);
      border: 2px solid ${isSelected ? modalState.accentColor : catColor};
      border-radius: 8px;
      padding: 12px;
      cursor: pointer;
      transition: all 0.2s;
      ${isSelected ? `box-shadow: 0 0 15px ${modalState.accentColor}80;` : ''}
    ">
      <div style="
        background: ${isSelected ? modalState.accentColor : 'rgba(255,255,255,0.1)'};
        color: ${isSelected ? '#fff' : '#888'};
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 0.85rem;
      ">${isSelected ? '✓' : ''}</div>
      
      <div style="
        width: 40px;
        height: 50px;
        background: linear-gradient(135deg, ${catColor}44 0%, ${catColor}22 100%);
        border: 2px solid ${catColor};
        border-radius: 6px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      ">
        <span style="font-size: 1rem;">${catIcon}</span>
        <span style="font-size: 0.9rem; font-weight: bold; color: #ffd700;">⚡${card.currentPower}</span>
      </div>
      
      <div style="flex: 1;">
        <div style="color: #fff; font-weight: bold; font-size: 0.9rem;">
          ${ownerLabel} ${def.name}
        </div>
        <div style="color: #888; font-size: 0.7rem;">パワー: ${card.currentPower}</div>
      </div>
      
      <button class="move-card-detail" data-idx="${idx}" style="
        background: transparent;
        border: 2px solid #9b59b6;
        color: #9b59b6;
        padding: 8px 12px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 0.8rem;
        font-weight: bold;
      ">詳細</button>
    </div>
  `;
}

function bindMoveEvents(): void {
  if (!modalState) return;
  
  const cards = document.querySelectorAll('.move-card');
  cards.forEach((card) => {
    card.addEventListener('click', handleSelectClick);
  });

  // 詳細ボタン
  document.querySelectorAll('.move-card-detail').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      const idx = parseInt((e.currentTarget as HTMLElement).getAttribute('data-idx') ?? '0', 10);
      const card = modalState?.cards[idx];
      if (card) showCardDetail(card);
    }, { capture: true });
  });

  document.getElementById('btnMoveSkip')?.addEventListener('click', handleSkip);
  document.getElementById('btnMoveConfirm')?.addEventListener('click', handleConfirm);
}

function handleSelectClick(e: Event): void {
  if (!modalState) return;
  
  const el = e.currentTarget as HTMLElement;
  const idx = parseInt(el.getAttribute('data-move-idx') ?? '0', 10);

  if (modalState.selectedIndices.has(idx)) {
    modalState.selectedIndices.delete(idx);
  } else if (modalState.selectedIndices.size < modalState.maxSelect) {
    modalState.selectedIndices.add(idx);
  }
  
  renderMoveModal();
  bindMoveEvents();
}

function handleSkip(): void {
  if (!modalState) return;
  
  const callback = modalState.onComplete;
  closeModal();
  callback([]);
}

function handleConfirm(): void {
  if (!modalState) return;
  if (modalState.selectedIndices.size === 0) return;
  
  const selected = Array.from(modalState.selectedIndices).map(idx => modalState!.cards[idx]);
  const callback = modalState.onComplete;
  closeModal();
  callback(selected);
}

function closeModal(): void {
  const modal = document.getElementById('moveCardModal');
  if (modal) modal.remove();
  modalState = null;
}

// カテゴリー色取得
function getCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    castle: '#8b4513', city: '#4682b4', space: '#483d8b', movie: '#dc143c',
    ship: '#20b2aa', ghost: '#4b0082', park: '#ff69b4',
    warrior: '#e74c3c', thief: '#9b59b6', mage: '#3498db', machine: '#95a5a6', undead: '#1abc9c',
  };
  return colors[category] || '#666';
}

// カテゴリーアイコン取得
function getCategoryIcon(category: string): string {
  const icons: Record<string, string> = {
    castle: '🏰', city: '🏙️', space: '🚀', movie: '🎬', ship: '🚢', ghost: '👻', park: '🎡',
    warrior: '⚔️', thief: '🗡️', mage: '🔮', machine: '⚙️', undead: '💀',
  };
  return icons[category] ?? '?';
}
